/**
 * Recharge Purchase Option Event Dispatcher
 * Dispatches recharge:purchase-option:change events when purchase options are selected
 */

(function () {
  'use strict';

  class RechargePurchaseOptionObserver {
    constructor() {
      this.initialized = false;
      this.selectionObserver = null;
      this.widgetContentObserver = null;
      this.rechargeContainer = null;
      this.currentSelectedElement = null;

      this.init();
    }

    init() {
      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setup());
      } else {
        this.setup();
      }
    }

    setup() {
      console.log('[RechargePurchaseOptionObserver] Setup starting...');

      // Only run on product pages
      if (!this.isProductPage()) {
        console.log('[RechargePurchaseOptionObserver] Not a product page, exiting');
        return;
      }

      console.log('[RechargePurchaseOptionObserver] Product page detected');

      this.findRechargeContainer();
      this.setupVariantChangeListener();
      this.startObserving();

      this.initialized = true;
      console.log(
        '[RechargePurchaseOptionObserver] Setup completed, initialized:',
        this.initialized
      );
    }

    isProductPage() {
      return document.querySelector('[data-section-type="product"]') !== null;
    }

    findRechargeContainer() {
      console.log('[RechargePurchaseOptionObserver] Looking for Recharge container...');

      // Look for Recharge custom elements and app blocks
      const rechargeSelectors = [
        'recharge-subscription-widget',
        '[id*="shopify-app-block"][id*="recharge"]',
        '[id*="recharge"]',
      ];

      for (const selector of rechargeSelectors) {
        const elements = document.querySelectorAll(selector);
        console.log(
          '[RechargePurchaseOptionObserver] Found elements for',
          selector + ':',
          elements.length
        );

        for (const element of elements) {
          console.log(
            '[RechargePurchaseOptionObserver] Checking element:',
            element.tagName,
            element.id || 'no-id'
          );
          const shadowRoot = this.findShadowRoot(element);
          if (shadowRoot) {
            this.rechargeContainer = shadowRoot;
            console.log(
              '[RechargePurchaseOptionObserver] Shadow root found in:',
              element.tagName
            );
            return;
          }
        }
      }

      if (!this.rechargeContainer) {
        console.warn('[RechargePurchaseOptionObserver] No Recharge shadow root found');
      }
    }

    setupVariantChangeListener() {
      // Listen for theme variant change event
      document.addEventListener(
        'theme:variant:change',
        this.handleVariantChange.bind(this)
      );
      
      console.log('[RechargePurchaseOptionObserver] Variant change listener setup');
    }

    handleVariantChange(event) {
      console.log('[RechargePurchaseOptionObserver] Theme variant change detected', event.detail);
      
      // Give the Recharge widget time to update after variant change
      setTimeout(() => {
        this.checkForSelectedOptionAfterVariantChange();
      }, 500);
      
      // Also check with a longer delay in case the widget updates slowly
      setTimeout(() => {
        this.checkForSelectedOptionAfterVariantChange();
      }, 1500);
    }

    checkForSelectedOptionAfterVariantChange() {
      if (!this.rechargeContainer) {
        console.log('[RechargePurchaseOptionObserver] No recharge container, attempting to find it');
        this.findRechargeContainer();
      }

      const observeTarget = this.rechargeContainer || 
        document.querySelector('recharge-subscription-widget');

      if (!observeTarget) {
        console.log('[RechargePurchaseOptionObserver] No recharge widget found after variant change');
        return;
      }

      // Look for currently selected option
      const selectedElement = observeTarget.querySelector('[part*="rc-purchase-option__selected"]');
      
      // Check if subscription options are available for this variant
      const hasSubscriptionOptions = this.hasSubscriptionOptionsAvailable(observeTarget);
      
      if (selectedElement && hasSubscriptionOptions) {
        console.log('[RechargePurchaseOptionObserver] Found selected option after variant change with subscription options available');
        
        // Check if this is a different selection than before
        if (selectedElement !== this.currentSelectedElement) {
          console.log('[RechargePurchaseOptionObserver] Selected option changed due to variant change');
          this.handlePurchaseOptionSelected(selectedElement);
        } else {
          // Same selection but may have different prices due to variant change
          console.log('[RechargePurchaseOptionObserver] Same option selected but checking for price changes due to variant');
          const priceData = this.extractPriceData(selectedElement);
          
          if (priceData) {
            console.log('[RechargePurchaseOptionObserver] Variant change - Dispatching recharge:selection:change event');
            this.dispatchPurchaseOptionChangeEvent(selectedElement, priceData, 'variant-change');
          }
        }
      } else if (selectedElement && !hasSubscriptionOptions) {
        console.log('[RechargePurchaseOptionObserver] Selected option found but no subscription options available - not dispatching event');
        
        // Clear current selection since no subscription options are available
        if (this.currentSelectedElement) {
          console.log('[RechargePurchaseOptionObserver] Clearing current selection - no subscription options for this variant');
          this.currentSelectedElement = null;
        }
      } else {
        console.log('[RechargePurchaseOptionObserver] No selected option found after variant change');
        
        // Clear current selection if no option is selected
        if (this.currentSelectedElement) {
          console.log('[RechargePurchaseOptionObserver] Clearing current selection due to variant change');
          this.currentSelectedElement = null;
        }
      }
    }

    hasSubscriptionOptionsAvailable(observeTarget) {
      // Check if there are subscription-related elements in the widget
      const subscriptionElements = observeTarget.querySelectorAll(
        '[part*="subscription"], [part*="rc-purchase-option__unit-price"], [part*="rc-purchase-option__discounted-price"]'
      );
      
      // Also check for subscription text content or labels
      const subscriptionText = observeTarget.textContent || '';
      const hasSubscriptionText = /subscription|subscribe|recurring|delivery/i.test(subscriptionText);
      
      const hasOptions = subscriptionElements.length > 0 || hasSubscriptionText;
      
      console.log('[RechargePurchaseOptionObserver] Subscription options available:', hasOptions, 
                  'Elements:', subscriptionElements.length, 
                  'Text match:', hasSubscriptionText);
      
      return hasOptions;
    }

    findShadowRoot(element) {
      // Check if element has shadow root
      if (element.shadowRoot) {
        return element.shadowRoot;
      }

      // Check child elements for shadow roots
      for (const child of element.children) {
        if (child.shadowRoot) {
          return child.shadowRoot;
        }

        // Recursively check nested elements
        const nestedShadowRoot = this.findShadowRoot(child);
        if (nestedShadowRoot) {
          return nestedShadowRoot;
        }
      }

      return null;
    }

    startObserving() {
      console.log('[RechargePurchaseOptionObserver] Starting to observe...');

      // Start observing the product form area for the Recharge widget to appear
      this.observeForRechargeWidget();
    }

    observeForRechargeWidget() {
      console.log(
        '[RechargePurchaseOptionObserver] Setting up widget detection observer...'
      );

      // Look specifically for the recharge widget container
      const rechargeWidget = document.querySelector(
        'recharge-subscription-widget'
      );

      if (rechargeWidget) {
        console.log(
          '[RechargePurchaseOptionObserver] Found existing recharge-subscription-widget'
        );
        this.checkForExistingWidget();
        return;
      }

      // If not found, observe document for the widget to appear
      console.log(
        '[RechargePurchaseOptionObserver] Observing document for recharge-subscription-widget to appear'
      );

      this.widgetObserver = new MutationObserver((mutations) => {
        this.handleWidgetMutations(mutations);
      });

      // Only observe the document root since we're looking for the specific widget
      this.widgetObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });

      // Also check if widget is already present (fallback)
      this.checkForExistingWidget();
    }

    handleWidgetMutations(mutations) {
      let foundWidget = false;

      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          for (const node of mutation.addedNodes) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check specifically for recharge-subscription-widget
              if (
                node.tagName === 'RECHARGE-SUBSCRIPTION-WIDGET' ||
                (node.querySelector &&
                  node.querySelector('recharge-subscription-widget'))
              ) {
                console.log(
                  '[RechargePurchaseOptionObserver] recharge-subscription-widget detected in DOM!'
                );
                foundWidget = true;
                break;
              }
            }
          }
        }
      }

      if (foundWidget) {
        // Give the widget a moment to fully initialize
        setTimeout(() => {
          this.findRechargeContainer();
          if (this.rechargeContainer) {
            console.log(
              '[RechargePurchaseOptionObserver] Switching to selection observation'
            );
            this.stopWidgetObserver();
            this.setupSelectionObserver();
          } else {
            console.log(
              '[RechargePurchaseOptionObserver] Widget found but no shadow root yet, continuing to observe...'
            );
          }
        }, 500);
      }
    }

    checkForExistingWidget() {
      console.log(
        '[RechargePurchaseOptionObserver] Checking for existing Recharge widget...'
      );

      this.findRechargeContainer();
      if (this.rechargeContainer) {
        console.log(
          '[RechargePurchaseOptionObserver] Found existing widget, switching to selection observation'
        );
        this.stopWidgetObserver();
        this.setupSelectionObserver();
      } else {
        console.log(
          '[RechargePurchaseOptionObserver] No existing widget found, will wait for it to load'
        );
      }
    }

    stopWidgetObserver() {
      if (this.widgetObserver) {
        this.widgetObserver.disconnect();
        this.widgetObserver = null;
        console.log('[RechargePurchaseOptionObserver] Widget observer stopped');
      }
    }

    setupSelectionObserver() {
      console.log(
        '[RechargePurchaseOptionObserver] Setting up selection observer...'
      );

      const observeTarget =
        this.rechargeContainer ||
        document.querySelector('recharge-subscription-widget');

      if (!observeTarget) {
        console.warn(
          '[RechargePurchaseOptionObserver] No recharge widget found to observe'
        );
        return;
      }

      // Create observer for selection changes
      this.selectionObserver = new MutationObserver((mutations) => {
        this.handleSelectionMutations(mutations);
      });

      // Observe the entire widget for changes
      this.selectionObserver.observe(observeTarget, {
        attributes: true,
        attributeFilter: ['part'],
        subtree: true,
      });

      console.log('[RechargePurchaseOptionObserver] Selection observer started');
      
      // Set up observer to detect when widget content is loaded
      this.setupWidgetContentObserver(observeTarget);
    }

    setupWidgetContentObserver(observeTarget) {
      console.log('[RechargePurchaseOptionObserver] Setting up widget content observer');
      
      // First check if content is already loaded
      if (this.isWidgetContentLoaded(observeTarget)) {
        console.log('[RechargePurchaseOptionObserver] Widget content already loaded');
        this.checkForInitialSelection();
        return;
      }
      
      // Set up observer to detect when price elements appear (indicating widget is loaded)
      this.widgetContentObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'childList') {
            // Check if widget content is now loaded
            if (this.isWidgetContentLoaded(observeTarget)) {
              console.log('[RechargePurchaseOptionObserver] Widget content loaded, checking for initial selection');
              
              // Stop observing and check for initial selection
              this.widgetContentObserver.disconnect();
              this.widgetContentObserver = null;
              
              this.checkForInitialSelection();
              return;
            }
          }
        }
      });

      // Observe for content changes
      this.widgetContentObserver.observe(observeTarget, {
        childList: true,
        subtree: true
      });
      
      console.log('[RechargePurchaseOptionObserver] Widget content observer started');
    }

    isWidgetContentLoaded(observeTarget) {
      // Check if the widget has price elements (indicates it's fully loaded)
      const priceElements = observeTarget.querySelectorAll(
        '[part="rc-purchase-option__price"], [part="rc-purchase-option__unit-price"], [part="rc-purchase-option__discounted-price"]'
      );
      
      const hasContent = priceElements.length > 0;
      console.log('[RechargePurchaseOptionObserver] Widget content loaded check:', hasContent, 'price elements found:', priceElements.length);
      
      return hasContent;
    }

    checkForInitialSelection() {
      console.log('[RechargePurchaseOptionObserver] Checking for initial selection on page load');
      
      const observeTarget =
        this.rechargeContainer ||
        document.querySelector('recharge-subscription-widget');

      if (!observeTarget) {
        console.log('[RechargePurchaseOptionObserver] No recharge widget found for initial check');
        return;
      }

      // Check if subscription options are available
      const hasSubscriptionOptions = this.hasSubscriptionOptionsAvailable(observeTarget);
      
      if (!hasSubscriptionOptions) {
        console.log('[RechargePurchaseOptionObserver] No subscription options available on page load');
        return;
      }

      // Look for currently selected option
      const selectedElement = observeTarget.querySelector('[part*="rc-purchase-option__selected"]');
      
      if (selectedElement) {
        console.log('[RechargePurchaseOptionObserver] Found initial selected option on page load');
        
        // Set current selected element and dispatch event
        this.currentSelectedElement = selectedElement;
        
        const priceData = this.extractPriceData(selectedElement);
        
        if (priceData) {
          console.log('[RechargePurchaseOptionObserver] Page load - Dispatching recharge:selection:change event');
          this.dispatchPurchaseOptionChangeEvent(selectedElement, priceData, 'initial-load');
        }
      } else {
        console.log('[RechargePurchaseOptionObserver] No selected option found on page load');
      }
    }

    handleSelectionMutations(mutations) {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'part') {
          const element = mutation.target;
          const partValue = element.getAttribute('part');

          // Check if rc-purchase-option__selected was added
          if (partValue && partValue.includes('rc-purchase-option__selected')) {
            console.log(
              '[RechargePurchaseOptionObserver] Selection detected on element:',
              element
            );
            this.handlePurchaseOptionSelected(element);
          }
        }
      }
    }

    handlePurchaseOptionSelected(selectedElement) {
      // Update current selected element
      this.currentSelectedElement = selectedElement;
      
      const priceData = this.extractPriceData(selectedElement);
      
      if (priceData) {
        console.log(
          '[RechargePurchaseOptionObserver] Selection change - Dispatching recharge:selection:change event',
          priceData
        );

        // Dispatch custom event
        this.dispatchPurchaseOptionChangeEvent(selectedElement, priceData, 'selection');
      }
    }

    dispatchPurchaseOptionChangeEvent(selectedElement, priceData, changeType) {
      // Dispatch custom event
      const event = new CustomEvent('recharge:selection:change', {
        detail: {
          selectedOption: selectedElement,
          price: priceData.price,
          planType: priceData.planType,
          hasDiscount: priceData.hasDiscount,
          priceElement: priceData.priceElement,
          changeType: changeType // 'selection', 'variant-change', or 'initial-load'
        },
        bubbles: true
      });

      document.dispatchEvent(event);
    }

    extractPriceData(selectedElement) {
      // Find the container that holds this selected element
      const container = selectedElement.closest('[part*="rc-purchase-option"]') || selectedElement;
      
      // Determine if this is a one-time or subscription option
      const isOneTime = this.isOneTimeOption(container);
      const isSubscription = this.isSubscriptionOption(container);
      
      let priceElement = null;
      let planType = 'unknown';
      let hasDiscount = false;

      if (isOneTime) {
        // For one-time purchases, look for rc-purchase-option__price
        priceElement = container.querySelector('[part="rc-purchase-option__price"]');
        planType = 'one-time';
        hasDiscount = false;
      } else if (isSubscription) {
        // For subscriptions, check for discounted price first, then unit price
        priceElement = container.querySelector('[part="rc-purchase-option__discounted-price"]');
        planType = 'subscription';
        hasDiscount = !!priceElement;

        if (!priceElement) {
          priceElement = container.querySelector('[part="rc-purchase-option__unit-price"]');
          hasDiscount = false;
        }
      }

      if (!priceElement) {
        console.warn(
          '[RechargePurchaseOptionObserver] No price element found for selected option'
        );
        return null;
      }

      const price = this.extractPriceFromElement(priceElement);

      if (!price || price <= 0) {
        console.warn(
          '[RechargePurchaseOptionObserver] Invalid price extracted:',
          price
        );
        return null;
      }

      return {
        price,
        planType,
        hasDiscount,
        priceElement
      };
    }

    isOneTimeOption(element) {
      // Check if this element or its parents indicate a one-time purchase option
      let current = element;
      let depth = 0;

      while (current && depth < 10) {
        const partValue = current.getAttribute && current.getAttribute('part');
        
        if (partValue && partValue.includes('onetime')) {
          return true;
        }

        current = current.parentNode;
        depth++;
      }

      return false;
    }

    isSubscriptionOption(element) {
      // Check if this element or its parents indicate a subscription option
      let current = element;
      let depth = 0;

      while (current && depth < 10) {
        const partValue = current.getAttribute && current.getAttribute('part');
        
        if (partValue && partValue.includes('subscription')) {
          return true;
        }

        current = current.parentNode;
        depth++;
      }

      return false;
    }

    extractPriceFromElement(element) {
      // Get all text content from the element and its children
      const text = element.textContent || element.innerText || '';

      // Try multiple regex patterns to extract price
      const patterns = [
        /\$([0-9,]+\.?\d*)/, // $29.99
        /([0-9,]+\.?\d*)\s*\$/, // 29.99$
        /([0-9,]+\.?\d+)/, // 29.99 (without $)
        /USD\s*([0-9,]+\.?\d*)/i, // USD 29.99
        /([0-9,]+\.?\d*)\s*USD/i, // 29.99 USD
      ];

      for (const pattern of patterns) {
        const match = text.match(pattern);
        if (match) {
          const price = parseFloat(match[1].replace(/,/g, ''));
          if (price > 0) {
            return price;
          }
        }
      }

      return 0;
    }

    destroy() {
      if (this.selectionObserver) {
        this.selectionObserver.disconnect();
        this.selectionObserver = null;
      }

      if (this.widgetContentObserver) {
        this.widgetContentObserver.disconnect();
        this.widgetContentObserver = null;
      }

      this.stopWidgetObserver();
      
      // Clean up variant change listener
      document.removeEventListener(
        'theme:variant:change',
        this.handleVariantChange
      );
      
      this.currentSelectedElement = null;
    }
  }

  // Initialize when page loads
  let purchaseOptionObserverInstance;

  function initRechargePurchaseOptionObserver() {
    if (purchaseOptionObserverInstance) {
      purchaseOptionObserverInstance.destroy();
    }

    purchaseOptionObserverInstance = new RechargePurchaseOptionObserver();
  }

  // Initialize on page load
  initRechargePurchaseOptionObserver();

  // Reinitialize on theme section load (for theme editor)
  document.addEventListener('shopify:section:load', initRechargePurchaseOptionObserver);
})();